import React, {
    forwardRef,
    useImperativeHandle,
    useState,
    useEffect,
    useCallback
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "../../atoms/Modal";
import {
    LOGGED_AZURE_TITLE,
    LOGIN_AZURE_TITLE,
    MESSAGES_STATUS_CHANGED,
    PURGE_CONFIRM
} from "../../../constants/contants";
import {purgeEmail, getSettings, authorizeToken} from "../../../api/emailTriage";
import loginAzure from "../LoginAzure";
import {LoadingPage} from "../../organisms/LoadingPage";
import {getUserData} from "../../../utils/common";


interface LocationState {
    service: string;
    account_name: string;
    account_email: string;
    message?: string;
    status?: boolean;
    sourceId?: string;
}

interface ModalContent {
    title: string;
    content: string;
    textAgree: string;
    variant: string;
    children: React.ReactNode;
    action?: string;
    onConfirm: () => void;
    onCancel?: () => void;
}

export const PhishClear = forwardRef((_, ref) => {
    const [loading, setLoading] = useState(false);
    const [isValidated, setIsValidated] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [contentModal, setContentModal] = useState<ModalContent | null>(null);
    const currentUser = getUserData()
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as LocationState | null;

    useEffect(() => {
        if (locationState) {
            navigate('.', { replace: true, state: null });
        }
    }, [locationState, navigate]);

    useEffect(() => {
        if (locationState && locationState.service.includes('phish_clear')) {
            handleLoginSuccess(locationState);
            setIsValidated(true);
        }
    }, []);

    const handleCloseAllPopup = () => {
        setOpenConfirm(false);
    };

    const handleLoginSuccess = useCallback((params: LocationState) => {
        const htmlContent = `
            <div>You’ve logged into <strong>${params.account_name}</strong>’s account. You can now use the remove emails in user’s inbox action.</div><br/>
            <div>
                We store Microsoft Purview authorisation for 90 days. These details will be used by PH2 Phish Focus portal 
                for purging emails from your organisation mailboxes. 
                You can cancel this anytime by terminating the refresh token under <strong>${params.account_name}</strong>’s account in Azure AD.
                You will be required to log in again to re-authorise after the 90 days period.
            </div>
        `;

        setContentModal({
            title: LOGGED_AZURE_TITLE,
            content: "",
            children: <div dangerouslySetInnerHTML={{ __html: htmlContent }} />,
            textAgree: 'OK',
            variant: 'success',
            action: '',
            onConfirm: handleCloseAllPopup
        });
        setOpenConfirm(true);
    }, []);

    const handleOpenConfirmRemovePopup = useCallback((onConfirm: () => void) => {
        const htmlContent = `
            <div>Phish Clear will find and remove emails that have the <strong>same subject</strong> and from the 
            <strong>same sender</strong> across your organisation mailboxes. ${PURGE_CONFIRM}</div>
            <div class="notes">
                <div>${MESSAGES_STATUS_CHANGED}: <span>Resolved</span></div>
            </div>
        `;

        setContentModal({
            title: `Remove emails from your organisation mailboxes`,
            content: "",
            children: <div dangerouslySetInnerHTML={{ __html: htmlContent }} />,
            textAgree: 'Remove',
            variant: 'delete',
            onConfirm
        });
        setOpenConfirm(true);
    }, []);

    const handleOpenUnauthorizedPopup = useCallback((expired: boolean, onConfirm: () => void) => {
        const contentHTML = expired
            ? `
                <div>
                    <div>Your Microsoft Purview credentials have expired. Please re-log into Microsoft 365 account with required Microsoft Purview eDiscovery Administrator credentials.</div>
                    <div>This will allow the purging of emails from the organisation mailboxes on your behalf.</div>
                </div>
              `
            : `
                <div>
                    <div>To perform this action, please log into Microsoft 365 account with required Microsoft Purview eDiscovery Administrator credentials.</div>
                    <div>This will allow the purging of emails from the organisation mailboxes on your behalf.</div>
                </div>
              `;

        setContentModal({
            title: LOGIN_AZURE_TITLE,
            content: "",
            children: <div dangerouslySetInnerHTML={{ __html: contentHTML }} />,
            textAgree: 'Log in',
            variant: 'default',
            onConfirm
        });
        setOpenConfirm(true);
    }, []);


    const openNoticePopup = useCallback((status: "success" | "error", emailList?: any) => {
        const isSuccess = status === "success";

        const title = isSuccess
            ? "Phish Clear has started!"
            : "Failed to remove email";

        const contentHTML = isSuccess
            ? `
            <div>
                We have started to find and remove emails that have the <strong>same subject</strong> and the 
                <strong>same sender</strong> across your organisation mailboxes. To view the status go to 
                Message Details > Activity logs or check the action details in the Microsoft Purview portal.
            </div>
            <div class="notes">
                <div>${MESSAGES_STATUS_CHANGED}: <span>Resolved</span></div>
            </div>`
            : `
            <div>There was an issue attempting to remove the selected email.</div>
            <div>Please try again later.</div>`;

        setContentModal({
            title,
            content: "",
            children: <div dangerouslySetInnerHTML={{ __html: contentHTML }} />,
            textAgree: "OK",
            variant: status,
            onConfirm: handleCloseAllPopup
        });

        setOpenConfirm(true);
    }, []);


    const performPhishClear = useCallback(
        (
            payload: any,
            onSuccess: (result: any) => void,
            onError: () => void
        ) => {
            const { service, sourceId, data } = payload;

            const retryLogin = () => {
                handleOpenUnauthorizedPopup(false, () => {
                    setLoading(true)
                    setOpenConfirm(false)
                    loginAzure({ service, sourceId });
                });
            };

            const callPhishClear = () => {
                setLoading(true)
                setOpenConfirm(false)
                purgeEmail(data)
                    .then((result:any) => {
                        onSuccess(result)
                        openNoticePopup("success");
                        setLoading(false)
                    }, (reason) => {
                        setLoading(false)
                        throw(reason)
                    })
                    .catch((error: any) => {
                        const errMsg = error.toLowerCase?.() ?? "";
                        if (errMsg === "unauthorized" || errMsg === "token expired") {
                            retryLogin();
                        } else {
                            onError();
                            openNoticePopup("error");
                        }
                        setLoading(false)
                    });
            };

            if (!isValidated) {
                setLoading(true)
                authorizeToken({
                    "identity_action": "ensure_valid_token",
                    "app_id":  process.env.REACT_APP_PHISH_FOCUS_AZURE_APP_ID,
                    "user_id": currentUser['id'],
                    "user_role": currentUser['role'],
                    "user_email": currentUser['email'],
                    "service": "phish_clear",
                    "service_key": "phish_clear"
                })
                    .then((response) => {
                        setLoading(false)
                        if (response?.data?.code=='SUCCESS') {
                            setIsValidated(true);
                            handleOpenConfirmRemovePopup(callPhishClear);
                        } else if (response?.data?.code=='ERROR_NONE_AUTHORIZED' || response?.data?.code=='TOKEN_EXPIRED') {
                            retryLogin();
                        } else {
                            throw('Error')
                        }
                    }).catch(() => {
                        console.error("Cannot get Phish Clear account data");
                        setLoading(false)
                        onError();
                    });
            } else {
                setLoading(false)
                handleOpenConfirmRemovePopup(callPhishClear);
            }
        },
        [isValidated, handleOpenConfirmRemovePopup, handleOpenUnauthorizedPopup]
    );

    useImperativeHandle(ref, () => ({
        performPhishClear
    }));

    return (
        <div>
            <Modal
                idModal="modal-phish-clear-service"
                openDialog={openConfirm}
                variant={contentModal?.variant}
                title={contentModal?.title}
                content={contentModal?.content}
                actionAgree={contentModal?.onConfirm}
                actionCancel={contentModal?.onCancel || handleCloseAllPopup}
                textAgree={contentModal?.textAgree || "Accept"}
                onClose={contentModal?.onCancel ? handleCloseAllPopup : undefined}
                children={contentModal?.children}
            />
            <LoadingPage open={loading}/>
        </div>
    );
});

export default PhishClear;
